import { FormEvent, useMemo, useState } from 'react';

type ApiObject = Record<string, unknown>;

type LiveFrame = {
  id?: string | number;
  can_id?: string | number;
  dlc?: string | number;
  data?: string | number[] | unknown;
  freq?: string | number;
  frequency?: string | number;
  chg?: string | number;
  changed?: string | number;
};

type FileEntry = {
  name?: string;
  filename?: string;
  size?: number;
};

const quickHosts = ['http://smartcan.local'];

function normalizeHost(value: string) {
  const trimmed = value.trim().replace(/\/$/, '');
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `http://${trimmed}`;
}

function formatValue(value: unknown, fallback = 'Unavailable') {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map((item) => String(item).padStart(2, '0')).join(' ');
  return String(value);
}

function explainError(error: unknown) {
  const message = error instanceof Error ? error.message : 'Unknown error';
  if (message.includes('timeout')) return 'API timeout. Confirm the device is powered, Wi-Fi is connected, and the selected host is correct.';
  if (message.includes('Failed to fetch')) return 'Device not reachable. This can be caused by Wi-Fi not connected, smartcan.local not resolving, CORS/browser limitations, HTTPS pages trying to call local HTTP devices, or the device being offline.';
  return message;
}

async function fetchJson<T>(base: string, path: string): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(new Error('timeout')), 5500);
  try {
    const response = await fetch(`${base}${path}`, { signal: controller.signal, cache: 'no-store' });
    if (!response.ok) throw new Error(`${path} returned HTTP ${response.status}`);
    return await response.json() as T;
  } finally {
    window.clearTimeout(timer);
  }
}

export function DashboardPage() {
  const [host, setHost] = useState('');
  const [status, setStatus] = useState<ApiObject | null>(null);
  const [live, setLive] = useState<LiveFrame[]>([]);
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [storage, setStorage] = useState<ApiObject | null>(null);
  const [state, setState] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [lastContact, setLastContact] = useState<string>('Never');
  const [error, setError] = useState<string>('');

  const baseUrl = useMemo(() => normalizeHost(host), [host]);

  async function connect(event?: FormEvent) {
    event?.preventDefault();
    const base = normalizeHost(host);
    if (!base) {
      setError('Enter a SmartCAN host or IP address.');
      setState('error');
      return;
    }

    setState('connecting');
    setError('');
    try {
      const nextStatus = await fetchJson<ApiObject>(base, '/api/status');
      setStatus(nextStatus);
      setLastContact(new Date().toLocaleTimeString());
      setState('connected');

      const liveResult = await fetchJson<LiveFrame[] | { frames?: LiveFrame[] }>(base, '/api/live').catch(() => []);
      setLive(Array.isArray(liveResult) ? liveResult : liveResult.frames ?? []);

      const storageResult = await fetchJson<ApiObject>(base, '/api/storage').catch(() => null);
      setStorage(storageResult);

      const filesResult = await fetchJson<FileEntry[] | { files?: FileEntry[] }>(base, '/api/files').catch(() => []);
      setFiles(Array.isArray(filesResult) ? filesResult : filesResult.files ?? []);
    } catch (nextError) {
      setState('error');
      setError(explainError(nextError));
    }
  }

  const statusCards = [
    ['Device', status?.device_name ?? status?.name],
    ['Logging', status?.logging_active ?? status?.logging],
    ['Frame Count', status?.frame_count],
    ['FPS', status?.fps],
    ['Drops', status?.drops ?? status?.ring_drops],
    ['Write Fail', status?.write_fail],
    ['Power', status?.battery_percent ? `${status.battery_percent}%` : status?.power_state ?? 'USB / unknown'],
    ['CAN Health', status?.can_health ?? (status?.can_bus_off ? 'Bus off' : 'Nominal')],
    ['Current File', status?.current_file]
  ];

  const diagnostics = [
    'can_spi_ok',
    'can_controller_alive',
    'can_bus_off',
    'can_tec',
    'can_rec',
    'ring_drops',
    'write_fail',
    'power_state',
    'shutdown_reason'
  ];

  return (
    <main className="dashboard-page">
      <section className="section dashboard-hero">
        <div>
          <p className="eyebrow">Wi-Fi Snapshot Mode</p>
          <h1>SmartCAN Device Dashboard</h1>
          <p className="lead">
            Connect to a SmartCAN Pro device over Wi-Fi for status, diagnostics, files, and lightweight live snapshots. For full high-rate live view, use USB with the VS Code Extension.
          </p>
          <div className="helper-panel">
            <h2>Connection helper</h2>
            <ul>
              <li>Connect this computer or phone to the SmartCAN Wi-Fi network.</li>
              <li>Enter the IP shown on your SmartCAN device.</li>
              <li>Try <code>http://smartcan.local</code> if mDNS is available on your network.</li>
              <li>If a browser blocks local HTTP requests from this HTTPS site, open the dashboard from a local network URL or use the VS Code Extension over USB.</li>
            </ul>
          </div>
        </div>
        <form className="connect-card" onSubmit={connect}>
          <label>Device IP or host<input value={host} onChange={(event) => setHost(event.target.value)} placeholder="IP shown on device, or smartcan.local" /></label>
          <div className="quick-hosts">
            {quickHosts.map((item) => <button key={item} type="button" onClick={() => setHost(item)}>{item.replace('http://', '')}</button>)}
          </div>
          <button className="button primary" disabled={state === 'connecting'} type="submit">{state === 'connecting' ? 'Connecting...' : 'Connect'}</button>
          <div className={`connection-state ${state}`}>State: {state}</div>
          <span>Last contact: {lastContact}</span>
          {error && <p className="error-text">{error}</p>}
        </form>
      </section>

      <section className="section">
        <div className="section-heading compact">
          <p className="eyebrow">Status</p>
          <h2>Device state</h2>
        </div>
        <div className="status-grid">
          {statusCards.map(([label, value]) => (
            <div className="status-card" key={label as string}>
              <small>{label as string}</small>
              <strong>{formatValue(value)}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="section dashboard-grid">
        <article className="data-panel wide">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Live Snapshot Table</p>
              <h2>/api/live</h2>
            </div>
            <button className="button secondary small" onClick={() => connect()} type="button">Refresh</button>
          </div>
          <div className="table-wrap">
            <table className="can-table live">
              <thead><tr><th>CAN ID</th><th>DLC</th><th>DATA</th><th>FREQ</th><th>CHG</th></tr></thead>
              <tbody>
                {live.length === 0 ? (
                  <tr><td colSpan={5}>No live snapshot loaded. Connect to a device or refresh.</td></tr>
                ) : live.map((frame, index) => (
                  <tr key={`${formatValue(frame.id ?? frame.can_id)}-${index}`}>
                    <td>{formatValue(frame.id ?? frame.can_id)}</td>
                    <td>{formatValue(frame.dlc)}</td>
                    <td>{formatValue(frame.data)}</td>
                    <td>{formatValue(frame.freq ?? frame.frequency)}</td>
                    <td>{formatValue(frame.chg ?? frame.changed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="panel-note">Wi-Fi dashboard uses Snapshot Mode. For full high-rate live view, use USB plus the VS Code Extension.</p>
        </article>

        <article className="data-panel">
          <p className="eyebrow">Logging Controls</p>
          <h2>Firmware actions</h2>
          <div className="control-row">
            <button className="button secondary" disabled title="Requires firmware API support.">Start Logging</button>
            <button className="button secondary" disabled title="Requires firmware API support.">Stop Logging</button>
          </div>
          <p className="panel-note">Controls are disabled until the firmware exposes a confirmed browser-safe logging API.</p>
        </article>

        <article className="data-panel">
          <p className="eyebrow">Storage</p>
          <h2>/api/storage</h2>
          <dl className="kv-list">
            {storage ? Object.entries(storage).map(([key, value]) => (
              <div key={key}><dt>{key}</dt><dd>{formatValue(value)}</dd></div>
            )) : <div><dt>Status</dt><dd>No storage response loaded.</dd></div>}
          </dl>
        </article>

        <article className="data-panel wide">
          <p className="eyebrow">Files</p>
          <h2>/api/files and /api/download</h2>
          <div className="file-list">
            {files.length === 0 ? <p>No CAP files loaded, or the files API is unavailable.</p> : files.map((file) => {
              const name = file.name ?? file.filename ?? 'capture.cap';
              const href = `${baseUrl}/api/download?file=${encodeURIComponent(name)}`;
              return (
                <div className="file-row" key={name}>
                  <span>{name}</span>
                  <small>{file.size ? `${file.size.toLocaleString()} bytes` : 'size unavailable'}</small>
                  <a className="button ghost small" href={href}>Download</a>
                </div>
              );
            })}
          </div>
        </article>

        <article className="data-panel wide">
          <p className="eyebrow">Diagnostics</p>
          <h2>CAN and device health</h2>
          <div className="diag-grid">
            {diagnostics.map((key) => (
              <div key={key}>
                <small>{key}</small>
                <strong>{formatValue(status?.[key])}</strong>
              </div>
            ))}
          </div>
        </article>
      </section>
    </main>
  );
}
